import React, { useState, useCallback } from 'react';
import { FaUpload, FaFileAlt, FaExclamationCircle, FaCheckCircle, FaTimes, FaDownload, FaArrowLeft, FaArrowRight, FaCheck, FaSpinner } from 'react-icons/fa';
import { importService, ImportColumn, ImportMapping, ImportPreview, ImportResult } from '../../services/importService';

interface ImportWizardProps {
  organizationId?: string;
  onComplete: (results: ImportResult) => void;
  onCancel: () => void;
}

const ImportWizard: React.FC<ImportWizardProps> = ({ organizationId = 'default-org-id', onComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [importType, setImportType] = useState<'students' | 'teachers'>('students');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [mapping, setMapping] = useState<ImportMapping>({});
  const [validationResult, setValidationResult] = useState<any>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const steps = [
    { id: 1, title: 'Tipo y Archivo', icon: FaFileAlt },
    { id: 2, title: 'Mapeo de Columnas', icon: FaArrowRight },
    { id: 3, title: 'Validación', icon: FaCheckCircle },
    { id: 4, title: 'Importación', icon: FaUpload }
  ];

  const handleFileSelect = useCallback(async (file: File) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await importService.parseFile(file);
      
      if (!result.success) {
        setError(result.message || 'Error al procesar el archivo');
        return;
      }

      setSelectedFile(file);
      setPreview(result.preview!);
      
      // Auto-map columns if possible
      const columns = importService.getAvailableColumns(importType);
      const autoMapping: ImportMapping = {};
      
      result.preview!.headers.forEach(header => {
        const normalizedHeader = header.toLowerCase().trim();
        const matchedColumn = columns.find(col => 
          col.label.toLowerCase().includes(normalizedHeader) ||
          col.field.toLowerCase().includes(normalizedHeader) ||
          normalizedHeader.includes(col.field.toLowerCase())
        );
        
        if (matchedColumn) {
          autoMapping[header] = matchedColumn.field;
        }
      });
      
      setMapping(autoMapping);
      
    } catch (error) {
      setError('Error al procesar el archivo');
    } finally {
      setLoading(false);
    }
  }, [importType]);

  const handleValidation = async () => {
    if (!selectedFile) return;

    try {
      setLoading(true);
      setError(null);

      const result = await importService.validateImportData(selectedFile, mapping, importType);
      
      setValidationResult(result);
      
      if (result.success) {
        setCurrentStep(4);
      }
      
    } catch (error) {
      setError('Error durante la validación');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    try {
      setLoading(true);
      setError(null);

      const result = await importService.importData(selectedFile, mapping, importType, organizationId);
      
      setImportResult(result);
      
      if (result.success) {
        onComplete(result);
      }
      
    } catch (error) {
      setError('Error durante la importación');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Tipo de Importación
        </label>
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => setImportType('students')}
            className={`p-4 border-2 rounded-lg text-left transition-colors ${
              importType === 'students' 
                ? 'border-blue-500 bg-blue-50 text-blue-700' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="font-medium">Estudiantes</div>
            <div className="text-sm text-gray-600">Importar datos de estudiantes</div>
          </button>
          <button
            onClick={() => setImportType('teachers')}
            className={`p-4 border-2 rounded-lg text-left transition-colors ${
              importType === 'teachers' 
                ? 'border-blue-500 bg-blue-50 text-blue-700' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="font-medium">Profesores</div>
            <div className="text-sm text-gray-600">Importar datos de profesores</div>
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Archivo Excel o CSV
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
            className="hidden"
            id="file-upload"
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            <FaUpload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <div className="text-lg font-medium text-gray-900 mb-2">
              Seleccionar archivo Excel (.xlsx) o CSV
            </div>
            <div className="text-sm text-gray-600">
              O arrastra y suelta tu archivo aquí
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Formatos soportados: .xlsx, .xls, .csv
            </div>
          </label>
        </div>
        
        {selectedFile && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <FaFileAlt className="text-green-600 mr-2" />
              <span className="font-medium text-green-800">{selectedFile.name}</span>
              <span className="ml-2 text-sm text-green-600">
                ({Math.round(selectedFile.size / 1024)} KB)
              </span>
            </div>
          </div>
        )}
      </div>

      {preview && (
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Vista Previa</h4>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {preview.headers.map((header, index) => (
                      <th key={index} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {preview.sampleData.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex} className="px-4 py-2 text-sm text-gray-900">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-600">
            Mostrando {preview.sampleData.length} de {preview.totalRows} filas
          </div>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => {
    const columns = importService.getAvailableColumns(importType);
    
    return (
      <div className="space-y-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Mapeo de Columnas</h4>
          <p className="text-sm text-gray-600 mb-4">
            Asocia cada columna de tu archivo CSV con los campos del sistema
          </p>
        </div>

        <div className="space-y-4">
          {preview?.headers.map((header, index) => (
            <div key={index} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
              <div className="flex-1">
                <div className="font-medium text-gray-900">{header}</div>
                <div className="text-sm text-gray-600">
                  Ejemplo: {preview.sampleData[0]?.[index] || 'N/A'}
                </div>
              </div>
              <div className="flex-1">
                <select
                  value={mapping[header] || ''}
                  onChange={(e) => setMapping(prev => ({
                    ...prev,
                    [header]: e.target.value
                  }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">-- No mapear --</option>
                  {columns.map(column => (
                    <option key={column.field} value={column.field}>
                      {column.label} {column.required && '*'}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <FaExclamationCircle className="text-blue-600 mt-0.5 mr-2" />
            <div className="text-sm text-blue-800">
              <div className="font-medium mb-1">Campos Requeridos</div>
              <div>
                Los siguientes campos son obligatorios: {' '}
                {columns.filter(col => col.required).map(col => col.label).join(', ')}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h4 className="font-medium text-gray-900 mb-3">Validación de Datos</h4>
        <p className="text-sm text-gray-600 mb-4">
          Revisando la integridad de los datos antes de la importación
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <FaSpinner className="animate-spin mx-auto h-8 w-8 text-blue-600 mb-4" />
          <div className="text-gray-600">Validando datos...</div>
        </div>
      ) : validationResult ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">{validationResult.totalRows || 0}</div>
              <div className="text-sm text-green-600">Filas totales</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-600">{validationResult.errors?.length || 0}</div>
              <div className="text-sm text-red-600">Errores encontrados</div>
            </div>
          </div>

          {validationResult.errors && validationResult.errors.length > 0 && (
            <div>
              <h5 className="font-medium text-gray-900 mb-3">Errores de Validación</h5>
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                {validationResult.errors.map((error: any, index: number) => (
                  <div key={index} className="p-3 border-b border-gray-200 last:border-b-0">
                    <div className="flex items-start">
                      <FaExclamationCircle className="text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          Fila {error.row}, Campo: {error.column}
                        </div>
                        <div className="text-sm text-red-600">{error.error}</div>
                        {error.value && (
                          <div className="text-xs text-gray-500 mt-1">
                            Valor: "{error.value}"
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {validationResult.success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <div className="text-green-800">
                  ¡Validación exitosa! Los datos están listos para importar.
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <button
            onClick={handleValidation}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors"
          >
            Iniciar Validación
          </button>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div>
        <h4 className="font-medium text-gray-900 mb-3">Importación de Datos</h4>
        <p className="text-sm text-gray-600 mb-4">
          Importando los datos validados al sistema
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <FaSpinner className="animate-spin mx-auto h-8 w-8 text-blue-600 mb-4" />
          <div className="text-gray-600">Importando datos...</div>
        </div>
      ) : importResult ? (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">{importResult.imported}</div>
              <div className="text-sm text-green-600">Registros importados</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-yellow-600">{importResult.duplicates}</div>
              <div className="text-sm text-yellow-600">Duplicados omitidos</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-600">{importResult.errors.length}</div>
              <div className="text-sm text-red-600">Errores</div>
            </div>
          </div>

          {importResult.success ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <div className="text-green-800">
                  ¡Importación completada exitosamente!
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <FaExclamationCircle className="text-red-600 mr-2" />
                <div className="text-red-800">
                  La importación se completó con errores. Revisa los detalles abajo.
                </div>
              </div>
            </div>
          )}

          {importResult.errors.length > 0 && (
            <div>
              <h5 className="font-medium text-gray-900 mb-3">Errores de Importación</h5>
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                {importResult.errors.map((error, index) => (
                  <div key={index} className="p-3 border-b border-gray-200 last:border-b-0">
                    <div className="flex items-start">
                      <FaExclamationCircle className="text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          Fila {error.row}
                        </div>
                        <div className="text-sm text-red-600">{error.error}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <button
            onClick={handleImport}
            className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg transition-colors"
          >
            Iniciar Importación
          </button>
        </div>
      )}
    </div>
  );

  const canProceedToNext = () => {
    switch (currentStep) {
      case 1: return selectedFile && preview;
      case 2: return Object.keys(mapping).length > 0;
      case 3: return validationResult?.success;
      case 4: return importResult?.success;
      default: return false;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Asistente de Importación</h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    isCompleted ? 'bg-green-500 border-green-500 text-white' :
                    isActive ? 'bg-blue-500 border-blue-500 text-white' :
                    'bg-gray-100 border-gray-300 text-gray-500'
                  }`}>
                    {isCompleted ? <FaCheck className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <div className="ml-3">
                    <div className={`text-sm font-medium ${
                      isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-4 ${
                      isCompleted ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <FaExclamationCircle className="text-red-600 mr-2" />
                <div className="text-red-800">{error}</div>
              </div>
            </div>
          )}

          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={onCancel}
            className="text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancelar
          </button>

          <div className="flex items-center space-x-3">
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <FaArrowLeft className="w-4 h-4" />
                <span>Anterior</span>
              </button>
            )}

            {currentStep < 4 && (
              <button
                onClick={() => setCurrentStep(prev => prev + 1)}
                disabled={!canProceedToNext() || loading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <span>Siguiente</span>
                <FaArrowRight className="w-4 h-4" />
              </button>
            )}

            {currentStep === 4 && importResult?.success && (
              <button
                onClick={() => onComplete(importResult)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                <FaCheck className="w-4 h-4" />
                <span>Finalizar</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportWizard;